import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import {
  FaSyncAlt,
  FaDownload,
  FaSearch,
  FaRulerCombined,
  FaMagic,
  FaSpinner,
  FaExternalLinkAlt,
} from 'react-icons/fa'
import {
  getEspecificacionesLimpias,
  getEspecificacionesDanadas,
  getRepuestosPorCodigoSap,
  getPlantillaMedidas,
  putUpdateRevision,
} from 'src/services/axiomaService'
import { getPermissions } from 'src/utils/Common'
import { permitsList } from 'src/constants/permits'
import { AXIOMA_AUDITOR, AXIOMA_AXIOMER } from 'src/routes/paths'
import MaterialDetalleModal from './MaterialDetalleModal'
import './AxiomaEspecificacionesContainer.css'

// Normaliza la respuesta del backend a un arreglo de afectados.
// Soporta { afectados: [...] }, { success: { afectados: [...] } } y arreglos directos.
const extractAfectados = (data) => {
  if (!data) return []
  const payload = data.success ?? data
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.afectados)) return payload.afectados
  return []
}

// Convierte el objeto medidas { A: 28.28, B: 10 } en pares [clave, valor] ordenados.
const medidasAEntradas = (medidas) => {
  if (!medidas || typeof medidas !== 'object') return []
  return Object.entries(medidas).sort((a, b) =>
    a[0].localeCompare(b[0], undefined, { numeric: true }),
  )
}

const formatearValor = (valor) => {
  if (valor === null || valor === undefined || valor === '') return '—'
  const num = Number(valor)
  return Number.isNaN(num) ? String(valor) : num.toLocaleString('es-CO')
}

// Aplica los filtros activos (búsqueda por código SAP y "solo con plantilla").
const filtrarFilas = (filas, busqueda, soloConPlantilla) => {
  const termino = busqueda.trim().toLowerCase()
  return filas.filter((fila) => {
    if (soloConPlantilla) {
      const plantillas = Array.isArray(fila.plantillas) ? fila.plantillas : []
      if (plantillas.length === 0) return false
    }
    if (!termino) return true
    return String(fila.codigo_material_sap ?? '')
      .toLowerCase()
      .includes(termino)
  })
}

const exportarCSV = (filas, tipo) => {
  const esLimpias = tipo === 'limpias'
  const nombreArchivo = esLimpias ? 'medidas_generadas' : 'medidas_ilegibles'
  const cabeceras = esLimpias
    ? [
        'Código Material SAP',
        'Medidas',
        'Plantillas Compatibles',
        'Especificaciones del Producto',
      ]
    : ['Código Material SAP', 'Especificaciones del Producto']
  const lineas = filas.map((fila) => {
    const celdas = esLimpias
      ? [
          fila.codigo_material_sap ?? '',
          medidasAEntradas(fila.medidas)
            .map(([clave, valor]) => `${clave}: ${formatearValor(valor)}`)
            .join(' | '),
          (fila.plantillas || []).join(' | '),
          fila.especificaciones_producto || '',
        ]
      : [fila.codigo_material_sap ?? '', fila.especificaciones_producto || '']
    return celdas.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')
  })
  const contenido = [cabeceras.join(','), ...lineas].join('\n')
  const blob = new Blob([`\uFEFF${contenido}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const enlace = document.createElement('a')
  enlace.href = url
  enlace.download = `${nombreArchivo}.csv`
  enlace.click()
  URL.revokeObjectURL(url)
}

// ===========================================================================
// "Aplicar plantilla" — deja el material EXACTAMENTE como si un axiomer lo
// hubiera editado y guardado a mano: mueve las medidas del texto libre
// (Especificaciones del Producto) a la plantilla de medidas (json_medidas) y
// borra las Especificaciones del Producto. Se reconstruye el MISMO payload que
// envía el formulario en un guardado normal (no finaliza, no reasigna),
// tomando todos los valores actuales del material para no alterar nada más.
// ===========================================================================

const extraerRepuestos = (respuesta) => {
  if (!respuesta) return []
  if (Array.isArray(respuesta.repuestos)) return respuesta.repuestos
  if (Array.isArray(respuesta?.success?.repuestos)) {
    return respuesta.success.repuestos
  }
  if (Array.isArray(respuesta.success)) return respuesta.success
  if (Array.isArray(respuesta)) return respuesta
  return []
}

// IDs de categoría/subcategoría para consultar la plantilla (mismos criterios
// que usa "Ver medidas" del formulario del axiomer).
const getCategoriaIds = (material) => ({
  categoriaId:
    material?.revision_categoria_sugerida_id ??
    material?.repuesto_categoria_id ??
    material?.categoria_id ??
    null,
  subcategoriaId:
    material?.revision_subcategoria_sugerida_id ??
    material?.repuesto_subcategoria_id ??
    material?.subcategoria_id ??
    null,
})

// tipos_proteccion_ids se guarda como array de objetos {id,...} (a veces JSON).
const parseProteccionIds = (material) => {
  const tp = material?.revision_tipos_proteccion
  if (!tp) return []
  try {
    const arr = typeof tp === 'string' ? JSON.parse(tp) : tp
    return Array.isArray(arr) ? arr.map((p) => p.id).filter(Boolean) : []
  } catch (error) {
    return []
  }
}

// Construye el json_medidas de la plantilla con los valores de las medidas ya
// parseadas (misma forma que guarda el formulario al aplicar una plantilla).
const construirJsonMedidas = (plantillaRaw, medidas, plantillaNombre, material) => {
  const plantilla = plantillaRaw?.data ?? plantillaRaw
  const especificaciones = plantilla?.especificaciones
  if (!Array.isArray(especificaciones) || especificaciones.length === 0) {
    return null
  }
  const gsUrl = plantilla.gs_url ?? ''
  const imagenReferencia =
    plantilla.imagen_referencia ??
    (gsUrl ? gsUrl.replace(/^.*\//, '').replace(/\.[^.]+$/, '') : '')
  const valorDeMedida = (medida) => {
    const clave = medida ?? ''
    const valor =
      medidas?.[clave] ?? medidas?.[String(clave).toUpperCase()] ?? ''
    return String(valor ?? '')
  }
  return {
    gs_url: gsUrl,
    imagenes_procesadas: Array.isArray(plantilla.imagenes_procesadas)
      ? plantilla.imagenes_procesadas
      : [],
    imagen_referencia: imagenReferencia,
    subcategoria:
      material?.revision_subcategoria_nombre ??
      material?.subcategoria_nombre ??
      '',
    categoria:
      material?.revision_categoria_nombre ?? material?.categoria_nombre ?? '',
    definicion_cotas: plantillaNombre ?? '',
    valor: 0,
    especificaciones: especificaciones.map((e) => ({
      medida: e.medida ?? '',
      descripcion: e.definicion_cotas ?? e.descripcion ?? '',
      definicion_cotas: e.definicion_cotas ?? e.descripcion ?? '',
      tipo_campo: e.tipo_campo ?? 'Num',
      opciones: e.opciones ?? '',
      valor: valorDeMedida(e.medida),
    })),
  }
}

// Reconstruye el payload de PUT /v1/revisiones idéntico a un "Guardar" normal
// del formulario (no finaliza / no reasigna), preservando todos los valores
// actuales del material y cambiando SOLO json_medidas y especificaciones_producto.
const construirPayloadRevision = (material, jsonMedidas) => ({
  revision_id: material.revision_id || null,
  repuesto_id: material.repuesto_id || null,
  usuario_id: material.revision_usuario_revisor_id || null,
  estado_revision:
    material.estado === 'REASIGNADO'
      ? 'REASIGNADO'
      : material.estado === 'AUDITADO'
      ? 'AUDITADO'
      : material.estado === 'REVISADO'
      ? 'REVISADO'
      : null,
  descripcion_breve: material.revision_descripcion_breve || null,
  descripcion_correcta: material.revision_descripcion_correcta || null,
  observacion: material.revision_observacion || null,
  cambio_etiqueta: material.revision_cambio_etiqueta ?? null,
  cambio_empaque: material.revision_cambio_empaque ?? null,
  cambio_descripcion: material.revision_cambio_descripcion ?? null,
  usuario_auditor_id: null,
  comentario_auditor: material.revision_comentario_auditor || null,
  estado_ax: material.revision_estado_ax || null,
  fecha_auditoria: null,
  largo_mm: material.revision_largo_mm || null,
  ancho_mm: material.revision_ancho_mm || null,
  alto_mm: material.revision_alto_mm || null,
  diametro_mm: material.revision_diametro_mm || null,
  json_medidas: jsonMedidas,
  peso_g: material.revision_peso_g || null,
  unidad_empaque_recomendada: material.revision_unidad_empaque_recomendada || null,
  uso_aplicacion: material.revision_uso_aplicacion,
  composicion: material.revision_composicion,
  especificaciones_producto: '',
  especificaciones_electricas: material.revision_especificaciones_electricas,
  tipo_empaque_sugerido_id: material.revision_tipo_empaque_sugerido_id,
  tipos_proteccion_ids: parseProteccionIds(material),
  embalaje_sugerido_id: material.revision_embalaje_sugerido_id || null,
  marca_fabrica_id:
    material.revision_marca_fabrica_id != null &&
    material.revision_marca_fabrica_id !== ''
      ? material.revision_marca_fabrica_id
      : null,
  marca_sugerida_id: material.revision_marca_sugerida_id || null,
  categoria_sugerida_id: material.revision_categoria_sugerida_id || null,
  subcategoria_sugerida_id: material.revision_subcategoria_sugerida_id || null,
  segmento_sugerido_id: material.revision_segmento_sugerido_id || null,
  subsegmento_sugerido_id: material.revision_subsegmento_sugerido_id || null,
  descripcion_breve_ia: material.revision_descripcion_breve_ia || '',
  descripcion_breve_ia_modificada: material.revision_descripcion_breve_ia
    ? material.revision_descripcion_breve_ia_modificada || 0
    : 0,
  descripcion_correcta_urgente: material.revision_descripcion_correcta_urgente
    ? 1
    : 0,
  imagen_favorita: material.revision_imagen_favorita || null,
  requiere_actualizacion_catalogo:
    material.revision_requiere_actualizacion_catalogo,
})

// Celda con chips clave/valor (medidas, especificaciones del producto).
const CeldaChips = ({ entradas, variante, vacioTexto }) => {
  if (entradas.length === 0) {
    return <span className="especificaciones-sin-medidas">{vacioTexto}</span>
  }
  return (
    <div className="especificaciones-medidas">
      {entradas.map(([clave, valor]) => (
        <span
          key={`${clave ?? ''}-${valor}`}
          className={`especificaciones-medida-chip ${variante || ''}`}
        >
          {clave && (
            <span className="especificaciones-medida-clave">{clave}</span>
          )}
          <span className="especificaciones-medida-valor">{valor}</span>
        </span>
      ))}
    </div>
  )
}

// Celda de acción: botón "Aplicar" (con selector de plantilla si hay varias).
const CeldaAplicar = ({
  fila,
  aplicando,
  plantillaSel,
  onCambiarPlantilla,
  onAplicar,
}) => {
  const plantillas = Array.isArray(fila.plantillas) ? fila.plantillas : []
  if (plantillas.length === 0) {
    return <span className="especificaciones-sin-medidas">Sin plantilla</span>
  }
  const seleccion = plantillaSel ?? plantillas[0]
  return (
    <div className="especificaciones-aplicar-wrap">
      {plantillas.length > 1 && (
        <select
          className="especificaciones-aplicar-select"
          value={seleccion}
          onChange={(e) =>
            onCambiarPlantilla(fila.codigo_material_sap, e.target.value)
          }
          disabled={aplicando}
        >
          {plantillas.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        className="especificaciones-btn-aplicar"
        onClick={() => onAplicar(fila, seleccion)}
        disabled={aplicando}
        title="Mover las medidas a la plantilla y borrar las Especificaciones del Producto"
      >
        {aplicando ? (
          <>
            <FaSpinner className="especificaciones-spin" />
            Aplicando…
          </>
        ) : (
          <>
            <FaMagic />
            Aplicar
          </>
        )}
      </button>
    </div>
  )
}

const TablaMedidas = ({
  filas,
  busqueda,
  tipo,
  soloConPlantilla,
  aplicandoCodigos,
  plantillaSeleccionada,
  onCambiarPlantilla,
  onAplicar,
  onAbrirDetalle,
  onAbrirEnAxioma,
}) => {
  const esLimpias = tipo === 'limpias'
  const filasFiltradas = useMemo(
    () => filtrarFilas(filas, busqueda, soloConPlantilla),
    [filas, busqueda, soloConPlantilla],
  )

  if (filasFiltradas.length === 0) {
    return (
      <div className="especificaciones-empty">
        {filas.length === 0
          ? 'No hay materiales para mostrar.'
          : 'Ningún material coincide con la búsqueda.'}
      </div>
    )
  }

  return (
    <div className="especificaciones-table-wrapper">
      <table className="especificaciones-table">
        <thead>
          <tr>
            <th className="especificaciones-col-codigo">Código Material</th>
            {esLimpias && <th>Medidas</th>}
            {esLimpias && <th>Plantillas Compatibles</th>}
            <th>Especificaciones del Producto</th>
            {esLimpias && (
              <th className="especificaciones-col-accion">Acción</th>
            )}
          </tr>
        </thead>
        <tbody>
          {filasFiltradas.map((fila) => {
            const entradas = medidasAEntradas(fila.medidas)
            const plantillas = Array.isArray(fila.plantillas)
              ? fila.plantillas
              : []
            return (
              <tr key={fila.codigo_material_sap}>
                <td className="especificaciones-col-codigo">
                  {fila.codigo_material_sap ? (
                    <div className="especificaciones-codigo-wrap">
                      <button
                        type="button"
                        className="especificaciones-codigo-link"
                        onClick={() => onAbrirDetalle?.(fila)}
                        title="Ver detalle del material"
                      >
                        {fila.codigo_material_sap}
                      </button>
                      <button
                        type="button"
                        className="especificaciones-codigo-abrir"
                        onClick={() => onAbrirEnAxioma?.(fila.codigo_material_sap)}
                        title="Abrir material en Axioma"
                      >
                        <FaExternalLinkAlt />
                      </button>
                    </div>
                  ) : (
                    '—'
                  )}
                </td>
                {esLimpias && (
                  <td>
                    <CeldaChips entradas={entradas} vacioTexto="Sin medidas" />
                  </td>
                )}
                {esLimpias && (
                  <td>
                    {plantillas.length === 0 ? (
                      <span className="especificaciones-sin-medidas">
                        Sin plantillas
                      </span>
                    ) : (
                      <div className="especificaciones-medidas">
                        {plantillas.map((plantilla) => (
                          <span
                            key={plantilla}
                            className="especificaciones-plantilla-chip"
                          >
                            {plantilla}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                )}
                <td>
                  {fila.especificaciones_producto ? (
                    <span className="especificaciones-texto-producto">
                      {fila.especificaciones_producto}
                    </span>
                  ) : (
                    <span className="especificaciones-sin-medidas">
                      Sin especificaciones
                    </span>
                  )}
                </td>
                {esLimpias && (
                  <td className="especificaciones-col-accion">
                    <CeldaAplicar
                      fila={fila}
                      aplicando={aplicandoCodigos.includes(
                        fila.codigo_material_sap,
                      )}
                      plantillaSel={
                        plantillaSeleccionada[fila.codigo_material_sap]
                      }
                      onCambiarPlantilla={onCambiarPlantilla}
                      onAplicar={onAplicar}
                    />
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const AxiomaEspecificacionesContainer = () => {
  const [limpias, setLimpias] = useState([])
  const [danadas, setDanadas] = useState([])
  const [tabActiva, setTabActiva] = useState('limpias')
  const [busqueda, setBusqueda] = useState('')
  const [soloConPlantilla, setSoloConPlantilla] = useState(false)
  const [loading, setLoading] = useState(true)
  const [aplicandoCodigos, setAplicandoCodigos] = useState([])
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState({})
  const [detalleFila, setDetalleFila] = useState(null)

  const cargarDatos = useCallback(async () => {
    setLoading(true)
    try {
      const [dataLimpias, dataDanadas] = await Promise.all([
        getEspecificacionesLimpias(),
        getEspecificacionesDanadas(),
      ])
      setLimpias(extractAfectados(dataLimpias))
      setDanadas(extractAfectados(dataDanadas))
    } catch (error) {
      console.error('Error al cargar especificaciones:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    cargarDatos()
  }, [cargarDatos])

  const filasActivas = tabActiva === 'limpias' ? limpias : danadas

  const handleExportar = () => {
    const esLimpias = tabActiva === 'limpias'
    const filasExportar = filtrarFilas(
      filasActivas,
      busqueda,
      esLimpias && soloConPlantilla,
    )
    exportarCSV(filasExportar, tabActiva)
  }

  const handleCambiarPlantilla = useCallback((codigo, plantilla) => {
    setPlantillaSeleccionada((prev) => ({ ...prev, [codigo]: plantilla }))
  }, [])

  const handleAbrirEnAxioma = useCallback((codigo) => {
    if (!codigo) return
    const permisos = getPermissions() || {}
    const esAuditor = Boolean(permisos[permitsList.AXIOMA_AUDITOR])
    const base = esAuditor ? AXIOMA_AUDITOR : AXIOMA_AXIOMER
    // Sin noopener: la nueva pestaña hereda sessionStorage (token/permisos).
    // Con noopener la sesión se pierde y PrivateRoute redirige a /login.
    window.open(
      `${base}?codigo=${encodeURIComponent(String(codigo))}`,
      '_blank',
    )
  }, [])

  const handleAplicar = useCallback(async (fila, plantillaNombre) => {
    const codigo = fila.codigo_material_sap
    if (!codigo || !plantillaNombre) {
      toast.warning('No hay una plantilla compatible para aplicar')
      return
    }
    setAplicandoCodigos((prev) => [...prev, codigo])
    try {
      // 1) Traer el material completo (mismos datos que carga el formulario).
      const respMaterial = await getRepuestosPorCodigoSap(String(codigo))
      const repuestos = extraerRepuestos(respMaterial)
      const material = repuestos[0]
      if (!material) {
        toast.error(`No se encontró el material ${codigo}`)
        return
      }

      // 2) Traer la plantilla y llenarla con las medidas ya parseadas.
      const { categoriaId, subcategoriaId } = getCategoriaIds(material)
      if (!categoriaId || !subcategoriaId) {
        toast.error(
          `El material ${codigo} no tiene categoría/subcategoría para la plantilla`,
        )
        return
      }
      const plantillaRaw = await getPlantillaMedidas(
        categoriaId,
        subcategoriaId,
        plantillaNombre,
      )
      const jsonMedidas = construirJsonMedidas(
        plantillaRaw,
        fila.medidas,
        plantillaNombre,
        material,
      )
      if (!jsonMedidas) {
        toast.error(`No se pudo cargar la plantilla "${plantillaNombre}"`)
        return
      }

      // 3) Guardar como un axiomer: plantilla llena + especificaciones vacías.
      const payload = construirPayloadRevision(material, jsonMedidas)
      const respuesta = await putUpdateRevision(payload)
      if (respuesta && respuesta.success) {
        toast.success(`Medidas actualizadas correctamente en el material ${codigo}`)
        // El material ya quedó corregido: se retira de la lista.
        setLimpias((prev) =>
          prev.filter((f) => f.codigo_material_sap !== codigo),
        )
      } else {
        toast.error(`No se pudo aplicar la plantilla al material ${codigo}`)
      }
    } catch (error) {
      console.error('Error al aplicar la plantilla:', error)
      toast.error(`Error al aplicar la plantilla al material ${codigo}`)
    } finally {
      setAplicandoCodigos((prev) => prev.filter((c) => c !== codigo))
    }
  }, [])

  return (
    <div className="especificaciones-container">
      <div className="especificaciones-header">
        <h1 className="especificaciones-title">
          <FaRulerCombined className="especificaciones-title-icon" />
          Especificaciones de Medidas
        </h1>
        <p className="especificaciones-subtitle">
          Revisa las medidas generadas y las medidas ilegibles de los materiales.
        </p>
      </div>

      <div className="especificaciones-toolbar">
        <div className="especificaciones-tabs">
          <button
            type="button"
            className={`especificaciones-tab ${
              tabActiva === 'limpias' ? 'active' : ''
            }`}
            onClick={() => setTabActiva('limpias')}
          >
            Medidas Generadas
            <span className="especificaciones-badge">{limpias.length}</span>
          </button>
          <button
            type="button"
            className={`especificaciones-tab ${
              tabActiva === 'danadas' ? 'active' : ''
            }`}
            onClick={() => setTabActiva('danadas')}
          >
            Medidas Ilegibles
            <span className="especificaciones-badge especificaciones-badge-warning">
              {danadas.length}
            </span>
          </button>
        </div>

        <div className="especificaciones-actions">
          <div className="especificaciones-search">
            <FaSearch className="especificaciones-search-icon" />
            <input
              type="text"
              placeholder="Buscar por código SAP..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          {tabActiva === 'limpias' && (
            <label className="especificaciones-check" htmlFor="filtro-plantilla">
              <input
                id="filtro-plantilla"
                type="checkbox"
                checked={soloConPlantilla}
                onChange={(e) => setSoloConPlantilla(e.target.checked)}
              />
              Solo con plantilla
            </label>
          )}
          <button
            type="button"
            className="especificaciones-btn"
            onClick={handleExportar}
            disabled={filasActivas.length === 0}
            title="Exportar CSV"
          >
            <FaDownload />
            Exportar
          </button>
          <button
            type="button"
            className="especificaciones-btn especificaciones-btn-primary"
            onClick={cargarDatos}
            title="Actualizar"
          >
            <FaSyncAlt />
            Actualizar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="especificaciones-loading">Cargando medidas...</div>
      ) : (
        <TablaMedidas
          filas={filasActivas}
          busqueda={busqueda}
          tipo={tabActiva}
          soloConPlantilla={tabActiva === 'limpias' && soloConPlantilla}
          aplicandoCodigos={aplicandoCodigos}
          plantillaSeleccionada={plantillaSeleccionada}
          onCambiarPlantilla={handleCambiarPlantilla}
          onAplicar={handleAplicar}
          onAbrirDetalle={setDetalleFila}
          onAbrirEnAxioma={handleAbrirEnAxioma}
        />
      )}

      <MaterialDetalleModal
        open={Boolean(detalleFila)}
        codigo={detalleFila?.codigo_material_sap}
        fila={detalleFila}
        onClose={() => setDetalleFila(null)}
      />
    </div>
  )
}

export default AxiomaEspecificacionesContainer
